import { usePartsReport } from '@/hooks/usePartsReport';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { DollarSign, Package, Calculator, Hash, Building2, User, Warehouse, TrendingUp, Receipt } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

interface PartsReportProps {
  startDate: Date;
  endDate: Date;
  timezone: string;
  compareEnabled?: boolean;
}

const formatNumber = (value: number) => {
  return new Intl.NumberFormat('en-AU').format(value);
};

// Metro row with expandable parts detail
function MetroPartsRow({ metro }: { metro: { metroId: string; metroName: string; partsCount: number; totalCost: number; totalRevenue: number; totalMargin: number; jobCount: number; averagePerJob: number; parts: { name: string; quantity: number; unitCost: number; unitSell: number; totalCost: number; revenue: number; margin: number }[] } }) {
  const [expanded, setExpanded] = useState(false);
  const { formatMoney: formatCurrency } = useCompanyLocale();

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <CollapsibleTrigger asChild>
        <div className="flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors">
          <div className="flex items-center gap-2">
            <ChevronDown className={cn(
              "h-4 w-4 text-muted-foreground transition-transform",
              expanded && "rotate-180"
            )} />
            <span className="font-medium">{metro.metroName}</span>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <span className="text-muted-foreground">{formatNumber(metro.partsCount)} parts</span>
            <span className="font-medium">{formatCurrency(metro.totalCost)} cost</span>
            <span className={cn("font-medium", metro.totalMargin < 0 && "text-destructive")}>
              {formatCurrency(metro.totalMargin)} margin
            </span>
            <span className="text-muted-foreground">{metro.jobCount} jobs</span>
            <span className="text-muted-foreground w-20 text-right">{formatCurrency(metro.averagePerJob)}/job</span>
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-6 mt-2 border-l-2 border-muted pl-4">
          {metro.parts.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">No parts data</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Part Name</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Unit Cost</TableHead>
                  <TableHead className="text-right">Unit Sell</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">Margin</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {metro.parts.map((part, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium">{part.name}</TableCell>
                    <TableCell className="text-right">{formatNumber(part.quantity)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(part.unitCost)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(part.unitSell)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(part.totalCost)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(part.revenue)}</TableCell>
                    <TableCell className={cn("text-right", part.margin < 0 && "text-destructive")}>{formatCurrency(part.margin)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function PartsReport({ startDate, endDate, timezone }: PartsReportProps) {
  const { data, isLoading, error } = usePartsReport({ startDate, endDate, timezone });
  const { formatMoney: formatCurrency } = useCompanyLocale();
  const [metroOpen, setMetroOpen] = useState(true);
  const [techOpen, setTechOpen] = useState(true);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-4 w-20 mb-2" />
                <Skeleton className="h-8 w-24" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-destructive">
          Failed to load parts report data.
        </CardContent>
      </Card>
    );
  }

  const { summary, topPartsByQuantity, topPartsByValue, byMetro, byTech, bySource } = data;

  // Empty state
  if (summary.totalQuantity === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No parts data for this period</h3>
          <p className="text-sm text-muted-foreground">
            Parts will appear here once jobs with parts are completed within the selected date range.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <DollarSign className="h-4 w-4" />
              <span className="text-xs font-medium">Total Cost</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(summary.totalCost)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Receipt className="h-4 w-4" />
              <span className="text-xs font-medium">Parts Revenue</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(summary.totalRevenue)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <TrendingUp className="h-4 w-4" />
              <span className="text-xs font-medium">Margin</span>
            </div>
            <p className={cn("text-2xl font-bold", summary.totalMargin < 0 && "text-destructive")}>
              {formatCurrency(summary.totalMargin)}
            </p>
            <p className="text-xs text-muted-foreground">{summary.marginPct.toFixed(1)}% of revenue</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Package className="h-4 w-4" />
              <span className="text-xs font-medium">Unique Parts</span>
            </div>
            <p className="text-2xl font-bold">{formatNumber(summary.uniqueParts)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Calculator className="h-4 w-4" />
              <span className="text-xs font-medium">Avg/Job</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(summary.averageCostPerJob)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Hash className="h-4 w-4" />
              <span className="text-xs font-medium">Total Qty</span>
            </div>
            <p className="text-2xl font-bold">{formatNumber(summary.totalQuantity)}</p>
          </CardContent>
        </Card>
      </div>
      <p className="text-xs text-muted-foreground -mt-3">
        Revenue is what customers were charged for parts; margin is revenue minus cost. Jobs closed before
        sell prices were introduced (16 Sep 2026) carry zero margin.
      </p>

      {/* Top Parts - Side by Side */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Top Parts by Quantity</CardTitle>
            <CardDescription>Most frequently used parts</CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {topPartsByQuantity.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No data</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Part</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topPartsByQuantity.map((part, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium truncate max-w-[150px]">{part.name}</TableCell>
                      <TableCell className="text-right">{formatNumber(part.quantity)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(part.totalCost)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Top Parts by Value</CardTitle>
            <CardDescription>Highest spend parts</CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {topPartsByValue.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No data</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Part</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topPartsByValue.map((part, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium truncate max-w-[150px]">{part.name}</TableCell>
                      <TableCell className="text-right">{formatCurrency(part.totalCost)}</TableCell>
                      <TableCell className="text-right">{formatNumber(part.quantity)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Source Breakdown */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Warehouse className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">Parts by Source</CardTitle>
          </div>
          <CardDescription>Company stock vs technician personal parts</CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {bySource.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No source data</p>
          ) : (
            <div className="space-y-3">
              {bySource.map((source, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className={cn(
                        "w-3 h-3 rounded-full",
                        source.source === 'Company Stock' ? 'bg-primary' : 'bg-muted-foreground'
                      )}
                    />
                    <span className="font-medium">{source.source}</span>
                  </div>
                  <div className="flex items-center gap-4 text-right">
                    <span className="text-muted-foreground text-sm">{formatNumber(source.quantity)} parts</span>
                    <span className="font-semibold">{formatCurrency(source.totalCost)}</span>
                    <span className="text-sm text-muted-foreground w-12">
                      {source.percentage.toFixed(0)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Metro Breakdown with Parts Drill-Down */}
      <Collapsible open={metroOpen} onOpenChange={setMetroOpen}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="pb-3 cursor-pointer hover:bg-muted/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <CardTitle className="text-base">Breakdown by Metro</CardTitle>
                </div>
                <ChevronDown className={cn(
                  "h-4 w-4 text-muted-foreground transition-transform",
                  metroOpen && "rotate-180"
                )} />
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0">
              {byMetro.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No metro data</p>
              ) : (
                <div className="space-y-2">
                  {byMetro.map((metro) => (
                    <MetroPartsRow key={metro.metroId} metro={metro} />
                  ))}
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Tech Breakdown */}
      <Collapsible open={techOpen} onOpenChange={setTechOpen}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="pb-3 cursor-pointer hover:bg-muted/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <CardTitle className="text-base">Breakdown by Technician</CardTitle>
                </div>
                <ChevronDown className={cn(
                  "h-4 w-4 text-muted-foreground transition-transform",
                  techOpen && "rotate-180"
                )} />
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0">
              {byTech.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No technician data</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Technician</TableHead>
                      <TableHead className="text-right">Parts</TableHead>
                      <TableHead className="text-right">Total Cost</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                      <TableHead className="text-right">Margin</TableHead>
                      <TableHead className="text-right">Jobs</TableHead>
                      <TableHead className="text-right">Avg/Job</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byTech.map((tech) => (
                      <TableRow key={tech.techId}>
                        <TableCell className="font-medium">{tech.techName}</TableCell>
                        <TableCell className="text-right">{formatNumber(tech.partsCount)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(tech.totalCost)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(tech.totalRevenue)}</TableCell>
                        <TableCell className={cn("text-right", tech.totalMargin < 0 && "text-destructive")}>{formatCurrency(tech.totalMargin)}</TableCell>
                        <TableCell className="text-right">{tech.jobCount}</TableCell>
                        <TableCell className="text-right">{formatCurrency(tech.averagePerJob)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
}
